import { Button, FormControlLabel, FormGroup, Switch, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useConfirmDialog } from 'common/hooks/useConfirmDialog';
import { fabric } from 'fabric';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import * as React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import isometricGrid from 'resources/isometricGrid.png';
import { AssetPanel } from './assets/AssetPanel';
import { ConfirmationDialog } from './ConfirmDialog';
import { useRoot } from './RootProvider';
import { useAssets } from './useAssets';
import { useFabric } from './useFabric';

type AssetEditorPageProps = {};

export const AssetEditorPage = observer((props: AssetEditorPageProps) => {
  const root = useRoot();
  const fabStore = useFabric();

  const params = useParams();
  const history = useHistory();
  const assets = useAssets();

  const { assetId } = params;

  const canvasRef = useRef(null);
  const [canvasElement, setCanvasElement] = React.useState(null);

  // Callback ref to ensure useEffect triggers when canvas element is ready
  const setCanvasRef = React.useCallback((element) => {
    canvasRef.current = element;
    setCanvasElement(element);
    console.log('🎭 Canvas ref set:', !!element);
  }, []);

  const [checked, setChecked] = React.useState(true);
  const [showLegend, setShowLegend] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loadAttempted, setLoadAttempted] = React.useState(false);

  const asset = assets.getAsset(assetId);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [openOri, handleInitOri, handleCancelOri, handleConfirmOri] = useConfirmDialog(
    asset?.deleteOrientation,
  );
  const [openPrim, handleInitPrim, handleCancelPrim, handleConfirmPrim] = useConfirmDialog(
    asset?.deletePrimitive,
  );

  const [openImage, handleInitImage, handleCancelImage, handleConfirmImage] = useConfirmDialog(
    asset?.deleteImage,
  );

  const handleGridLineChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
    fabStore.canvas.backgroundColor = checked ? null : fabStore.getGridLinePattern();
  };

  useEffect(() => {
    const loadAssetIfNeeded = async () => {
      if (assetId && !loading && !loadAttempted) {
        setLoading(true);
        setLoadAttempted(true);
        try {
          // Force refresh to get up-to-date image URLs and data on navigation
          const loadedAsset = await assets.handleLoadAssetById(assetId, true);
          if (!loadedAsset) {
            console.error('Asset not found:', assetId);
            history.replace('/');
            return;
          }
        } catch (error) {
          console.error('Failed to load asset:', error);
          history.replace('/');
          return;
        } finally {
          setLoading(false);
        }
      }
    };

    loadAssetIfNeeded();
  }, [assetId, asset, assets, history, loading, loadAttempted]);

  useEffect(() => {
    const hasCanvasElement = !!canvasElement;
    const hasFabCanvas = !!fabStore.canvas;

    console.log('🎭 Canvas setup useEffect triggered:', {
      hasAsset: !!asset,
      hasCanvasElement,
      hasFabCanvas,
      assetId: asset?.id,
      orientationsCount: asset?.orientations?.size,
    });

    // Ensure Fabric canvas is initialized once the DOM canvas is ready
    if (hasCanvasElement && !hasFabCanvas) {
      console.log('🎭 Setting up fabric canvas');
      fabStore.setCanvas(
        new fabric.Canvas(canvasElement, {
          renderOnAddRemove: true,
          preserveObjectStacking: true,
        }),
      );
      fabStore.canvas.backgroundColor = fabStore.getGridLinePattern();
    }

    // If Fabric canvas exists but is attached to a different DOM element (from a previous page),
    // rebind it to this page's canvas element.
    if (hasCanvasElement && hasFabCanvas && (fabStore.canvas as any)?.lowerCanvasEl !== canvasElement) {
      console.log('🔁 Rebinding fabric canvas to new DOM element');
      try {
        fabStore.canvas.dispose();
        // @ts-ignore reset reference so we can re-create
        fabStore.canvas = undefined;
      } catch {}
      fabStore.setCanvas(
        new fabric.Canvas(canvasElement, {
          renderOnAddRemove: true,
          preserveObjectStacking: true,
        }),
      );
      fabStore.canvas.backgroundColor = fabStore.getGridLinePattern();
    }

    // Whenever navigating to a different asset (or asset becomes available),
    // repopulate the canvas with the asset's orientation/images only after
    // the Fabric canvas is bound to the current DOM canvas element
    if (asset && hasCanvasElement && !!fabStore.canvas && (fabStore.canvas as any)?.lowerCanvasEl === canvasElement) {
      console.log('🎭 Populating canvas for asset:', asset.id);
      fabStore.dumpCanvas();
      asset.findDefaultOrientation();
    }
  }, [assetId, asset, canvasElement, fabStore]);

  // Show loading state while asset is being loaded
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Loading asset...</Typography>
      </Box>
    );
  }

  // Only redirect if we've attempted to load and asset is still not found
  if (!asset && !loading && loadAttempted) {
    history.replace('/');
    return null;
  }

  // If we haven't loaded the asset yet, show loading (this prevents immediate redirect)
  if (!asset && !loadAttempted) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography>Initializing...</Typography>
      </Box>
    );
  }

  assets.setCurrentAsset(assetId);

  return (
    <Box display='flex'>
      <Box>
        <AssetPanel
          id={assetId}
          onDeleteOri={handleInitOri}
          onDeletePrim={handleInitPrim}
          onDeleteImage={handleInitImage}
        />
      </Box>
      <Box>
        <Box 
          display='flex' 
          flexDirection='column' 
          sx={{ 
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fafafa',
            padding: 2,
            margin: 2,
            height: '100%'
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Typography variant='h6' sx={{ fontWeight: 'bold', color: '#333' }}>
              Preview
            </Typography>
            <Button
              size='small'
              variant='outlined'
              onClick={() => setShowLegend(!showLegend)}
              sx={{
                minWidth: 'auto',
                px: 1.5,
                py: 0.5,
                fontSize: '0.75rem',
                textTransform: 'none'
              }}
            >
              {showLegend ? 'Hide' : 'Show'} Controls
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              border: '2px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              position: 'relative',
              overflow: 'hidden',
              mb: 2
            }}
          >
            <canvas
              ref={setCanvasRef}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            />
          </Box>

          <FormGroup sx={{ mb: showLegend ? 2 : 0 }}>
            <FormControlLabel
              control={<Switch checked={checked} onChange={handleGridLineChange} />}
              label="Grid-line background"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
            />
          </FormGroup>

          {showLegend && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0'
              }}
            >
              <Typography variant='body2' sx={{ color: '#666', mb: 0.5, display: 'flex', alignItems: 'center' }}>
                🖱️ Mouse wheel to zoom • Alt + Click/Drag to pan
              </Typography>
              <Typography variant='body2' sx={{ color: '#666', mb: 0.5 }}>
                ⌨️ Press Q to cycle orientations
              </Typography>
              <Typography variant='body2' sx={{ color: '#666', mb: 0.5 }}>
                ⌨️ Press W to cycle images
              </Typography>
              <Typography variant='body2' sx={{ color: '#666' }}>
                ⌨️ Press E to cycle primitives
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <ConfirmationDialog
        open={openOri}
        onClose={handleCancelOri}
        onConfirm={handleConfirmOri}
        title={'Delete Orientation?'}
        message={'Please confirm'}
      />
      <ConfirmationDialog
        open={openImage}
        onClose={handleCancelImage}
        onConfirm={handleConfirmImage}
        title={'Delete Image?'}
        message={'Please confirm'}
      />
      <ConfirmationDialog
        open={openPrim}
        onClose={handleCancelPrim}
        onConfirm={handleConfirmPrim}
        title={'Delete Primititve?'}
        message={'Please confirm'}
      />
    </Box>
  );
});
